import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { Context } from 'src/app/shared/models/context';
import { PrivilegeType, TableData } from 'src/app/shared/models/domain';
import { LedgerReport } from 'src/app/shared/models/finance';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-ledger-report',
  templateUrl: './ledger-report.component.html',
  styleUrls: ['./ledger-report.component.scss']
})
export class LedgerReportComponent implements OnInit {
  public tableData: TableData = { content: [], number: 1, size: 10, totalElements: 0 } as TableData;
  public pageSizes: number[] = [];
  private pageDataKey: string = 'm-l';
  public selectedFilter: string = '';
  public filters: string[] = [];
  public fromDate: Date;
  public toDate: Date;
  public isLoading: boolean = true;
  public context: Context;
  public privilege: PrivilegeType;
  public searchKeyword: string = '';
  public types: string[] = [];
  public type: string;

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private financeService: FinanceService,
    private datePipe: DatePipe,
    private dataService: DataService,
    private domainService: DomainService
  ) { }

  ngOnInit(): void {
    this.dataService.privilege().subscribe(privilege => {
      this.privilege = this.commonService.getPrivileges(privilege);
    });
    this.types = this.commonService.getTypes();
    this.context = this.authService.getContextData();
    this.filters = this.domainService.getDateFiltersTypes();
    this.selectedFilter = this.filters[2] || 'Last Week';
    this.pageSizes = this.commonService.getDefaultPageSizes();
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.fromDate = this.commonService.adjustDateByDays(-30);
    this.toDate = new Date();
    if (this.context) {
      this.getLedgerReport();
    }
  }

  public getLedgerReport(clear: boolean = true, pageNumber: number = 0) {
    if (clear) {
      this.isLoading = true;
      this.tableData.content = [];
    }

    const pageSize = Number(this.tableData.size);
    const fromDate = this.fromDate ? this.datePipe.transform(this.fromDate, 'dd-MM-yyyy') : undefined;
    const toDate = this.toDate ? this.datePipe.transform(this.toDate, 'dd-MM-yyyy') : undefined;

    this.financeService.getLedgerReportByPge(pageNumber, pageSize, null, this.type, fromDate, toDate)
      .subscribe({
        next: (tableData: TableData) => {
          const content = tableData.content || [];
          this.tableData = {
            ...tableData,
            content: content.map(item => ({
              ...item,
              expanded: false,
              voucherDataList: item.voucherDataList || [],
            })),
            number: pageNumber + 1,
            size: pageSize,
            totalElements: tableData.totalElements || content.length,
            numberOfElements: content.length,
            empty: content.length === 0
          };
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || 'Failed to fetch ledger report.');
        }
      });
  }

  public toggleExpandRow(ledgerReport: LedgerReport) {
    this.tableData.content.forEach(item => {
      if (item !== ledgerReport) {
        item.expanded = false;
      }
    });
    ledgerReport.expanded = !ledgerReport.expanded;
  }

  public loadTableData(e: any) {
    this.tableData.size = +this.tableData.size;
    this.tableData.number = 1;
    this.commonService.storePageData(this.pageDataKey, this.tableData.size, this.tableData.number);
    this.getLedgerReport(true);
  }

  public onPageChange(newPage: number): void {
    this.tableData.number = newPage;
    const pageNumber = newPage - 1;
    this.getLedgerReport(false, pageNumber);
  }

  public filterAndFetchLedgerReport(filter: string, element?: any) {
    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0));
    let fromDate: Date;
    let toDate: Date;

    switch (filter) {
      case 'Today':
        fromDate = new Date(today);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Yesterday':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 1);
        toDate = new Date(today);
        toDate.setDate(today.getDate() - 1);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Last Week':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 7);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Custom':
        if (!this.fromDate || !this.toDate) {
          this.commonService.setToastr(400, 'Please select the date range!');
          return;
        }
        fromDate = new Date(this.fromDate);
        toDate = new Date(this.toDate);
        toDate.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    element?.close();
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.getLedgerReport();
  }

  public downloadExcel() {
    const excelData = this.tableData.content.map((ledger, index) => ({
      '#': index + 1,
      'Account Name': ledger.accountName,
      'Total Debits': ledger.totalDebits,
      'Total Credits': ledger.totalCredits,
      'Account Type': ledger.accountType
    }));
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Ledger Report': worksheet },
      SheetNames: ['Ledger Report']
    };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(data, `Ledger_Report_${this.datePipe.transform(new Date(), 'yyyy-MM-dd')}.xlsx`)
  }

}